import { useMutation } from "@apollo/client";
import { compact, sortBy, uniqBy } from "lodash";
import moment from "moment/moment";
import { useMemo, useState } from "react";
import {
  AccountToOrganizationMembershipStatus,
  InviteUsersMutation,
  InviteUsersMutationVariables,
  OrganizationMemberFragment,
  OrganizationRole,
  RemoveUserFromOrganizationMutationMutation,
  RemoveUserFromOrganizationMutationMutationVariables,
  UpdateMemberMutationMutation,
  UpdateMemberMutationMutationVariables,
  UserStatus,
} from "types/graphql-schema";

import inviteUsersMutation from "@apps/invite-dialog/graphql/invite-users-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";
import { TableBodyCell, TableBodyRow } from "@components/table/table";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { lastSeenStr } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import getOrganizationMembersQuery from "../graphql/get-organization-members-query";
import removeUserFromOrganizationMutation from "../graphql/remove-user-from-organization-mutation";
import updateMemberMutation from "../graphql/update-member-mutation";
import MemberEditDialog from "./member-edit-dialog";

const getPermissionLabel = (role?: OrganizationRole | null) => {
  switch (role) {
    case OrganizationRole.GlobalAdmin:
      return "Global Admin";
    case OrganizationRole.PeopleAdmin:
      return "People Admin";
    case OrganizationRole.ItAdmin:
      return "IT Admin";
    default:
      return "User";
  }
};

const MemberItem = ({
  organization,
  member,
  onRemoveUser,
}: {
  organization: any;
  member: OrganizationMemberFragment;
  onRemoveUser: (member: OrganizationMemberFragment) => void;
}) => {
  const label = useLabel();
  const [isEditing, setIsEditing] = useState(false);
  const [updateMember] = useMutation<
    UpdateMemberMutationMutation,
    UpdateMemberMutationMutationVariables
  >(updateMemberMutation);
  const [removeUserFromOrg, { loading: removeLoading }] = useMutation<
    RemoveUserFromOrganizationMutationMutation,
    RemoveUserFromOrganizationMutationMutationVariables
  >(removeUserFromOrganizationMutation);
  const [inviteUsers] = useMutation<
    InviteUsersMutation,
    InviteUsersMutationVariables
  >(inviteUsersMutation);

  const user = assertNonNull(member.user);
  const userId = user.id;
  const userName = user.name;
  const userEmail = user.email;
  const isActiveMember =
    member.membershipStatus === AccountToOrganizationMembershipStatus.Active;

  const {
    ConfirmationDialog: ConfirmationRemovalDialog,
    confirm: confirmRemoval,
  } = useConfirm(
    "Are you sure?",
    `Are you sure you want to remove this person from the ${label(
      "organization",
    )}?`,
  );
  const {
    ConfirmationDialog: ConfirmationDeactivationDialog,
    confirm: confirmDeactivation,
  } = useConfirm(
    "Are you sure?",
    `Are you sure you want to deactivate this person from the ${label(
      "organization",
    )}?`,
  );
  const {
    ConfirmationDialog: ConfirmationReactivationDialog,
    confirm: confirmReactivation,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to reactivate this person?",
  );
  const currentUser = currentUserVar();

  const primaryManagerId = member.user?.primaryManager?.id;
  const teams = member.user?.teams ? assertEdgesNonNull(member.user.teams) : [];

  const handleChangeMembershipStatus = (
    membershipStatus: AccountToOrganizationMembershipStatus,
  ) => {
    updateMember({
      variables: {
        organizationId: organization.id,
        membershipStatus,
        userId,
      },

      refetchQueries: [getOrganizationMembersQuery],
    });
  };

  const handleClickRemoveUser = async () => {
    const isConfirmed = await confirmRemoval();
    if (isConfirmed) {
      removeUserFromOrg({
        variables: {
          organizationId: organization.id,
          userToRemoveId: userId,
        },

        onCompleted: () => {
          successNotificationVar({
            title: "User removed.",
            timeout: 1000,
          });
          onRemoveUser(member);
        },
      });
    }
  };

  const handleClickDeactivateUser = async () => {
    const isConfirmed = await confirmDeactivation();
    if (isConfirmed) {
      handleChangeMembershipStatus(
        AccountToOrganizationMembershipStatus.Inactive,
      );
    }
  };
  const handleClickReactivateUser = async () => {
    const isConfirmed = await confirmReactivation();
    if (isConfirmed) {
      handleChangeMembershipStatus(
        AccountToOrganizationMembershipStatus.Active,
      );
    }
  };

  const handleClickInvite = () => {
    inviteUsers({
      variables: {
        invitationRequests: [
          {
            inviteeEmail: userEmail,
            inviteMessage: "",
            role: member.role || OrganizationRole.Member,
            organizationId: organization.id,
          },
        ],
      },

      onCompleted: (data) => {
        const error = data?.createInvitations?.invitationErrors?.[0];
        if (error) {
          errorNotificationVar({
            title: `${userEmail} could not be invited`,
            description: error.message || "",
          });
        } else {
          successNotificationVar({ title: "User invitation sent." });
        }
      },
    });
  };

  const sortedManagers = useMemo(() => {
    const managers = member.user?.managers
      ? assertEdgesNonNull(member.user.managers)
      : [];
    return sortBy(managers, (manager) => {
      if (manager.id === primaryManagerId) return "0-";
      return `1-${manager.name.toLowerCase()}`;
    });
  }, [member, primaryManagerId]);

  return (
    <TableBodyRow
      data-testid={userId}
      aria-label={`Org settings member: ${userName}`}
      className={classNames(!isActiveMember && "bg-gray-100")}
    >
      <TableBodyCell className="max-w-[350px]">
        <ConfirmationRemovalDialog />
        <ConfirmationDeactivationDialog />
        <ConfirmationReactivationDialog />
        {isEditing && (
          <MemberEditDialog
            member={member}
            organization={organization}
            onClose={() => setIsEditing(false)}
          />
        )}
        <div className="flex text-gray-800 items-center">
          <div className="mr-3 flex-none">
            <Avatar user={user} size="9" />
          </div>
          <div className="tracking-tight flex min-w-0 flex-col justify-center gap-0.5">
            <div className="truncate font-medium" title={user.name}>
              {user.name}
            </div>
            <div
              className="truncate text-xs text-gray-600"
              title={user.email ?? undefined}
            >
              {user.email}
            </div>
            <div className="truncate text-xs text-gray-400">
              {user.lastSeenStr ? lastSeenStr[user.lastSeenStr] : ""}
            </div>
          </div>
          {!isActiveMember && (
            <div className="ml-4 p-1.5 rounded-xl bg-gray-200 text-xs font-bold uppercase text-gray-500">
              Deactivated
            </div>
          )}
        </div>
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {sortedManagers.length > 0
          ? sortedManagers.map((manager) => (
              <div className="flex items-center gap-1" key={manager.name}>
                {manager.name}{" "}
                {manager.id === primaryManagerId && (
                  <Tooltip text="Primary manager">
                    <span>
                      <TopicflowIcon
                        type="star"
                        className="h-3 w-3 text-gray-400"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            ))
          : "No managers"}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {teams.length > 0
          ? teams.map((team) => <div key={team.title}>{team.title}</div>)
          : `No ${label("team", { pluralize: true })}`}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {member.employeeId || "None"}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {member.position || "None"}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {member.user?.currentRoles && member.user.currentRoles.length > 0
          ? uniqBy(member.user.currentRoles, (role) => role.careerTrack.id)
              .map((role) => `${role.careerTrack.title}`)
              .join(", ")
          : "None"}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {member.user?.currentRoles && member.user.currentRoles.length > 0
          ? member.user.currentRoles
              .map((role) => `${role.title} (L${role.level})`)
              .join(", ")
          : "None"}
      </TableBodyCell>

      <TableBodyCell className="text-sm">
        {member.startDate
          ? moment(member.startDate).format("LL")
          : "No start date"}
      </TableBodyCell>
      <TableBodyCell className="text-sm">
        {getPermissionLabel(member.role)}
      </TableBodyCell>
      <TableBodyCell>
        <div className="flex justify-end items-center">
          {removeLoading ? (
            <Loading mini size="5" />
          ) : (
            <Dropdown
              aria-label="Action dropdown"
              align="end"
              options={compact([
                user.status !== UserStatus.Active && {
                  label: "Re-invite",
                  onClick: handleClickInvite,
                },
                {
                  label: "Edit",
                  onClick: () => setIsEditing(true),
                },
                user.status === UserStatus.Active &&
                  isActiveMember &&
                  userId !== currentUser.id && {
                    label: "Deactivate",
                    onClick: handleClickDeactivateUser,
                  },
                user.status === UserStatus.Active &&
                  !isActiveMember && {
                    label: "Reactivate",
                    onClick: handleClickReactivateUser,
                  },
                currentUser.id !== userId && {
                  label: "Remove",
                  onClick: handleClickRemoveUser,
                },
              ])}
            />
          )}
        </div>
      </TableBodyCell>
    </TableBodyRow>
  );
};

export default MemberItem;
